// W149 · 2026-05-28
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { COMPANY_INFO } from '@/lib/company-info';
import type { AuthSession } from '@builder/shared';
import { loginRequestSchema } from '@builder/shared';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { PublicHeader } from '@/components/public/PublicHeader';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { AnimatedLogo } from '@/components/AnimatedLogo';
import { TurnstileWidget, type TurnstileHandle } from '@/components/security/TurnstileWidget';

export default function LoginPage() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuth((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const tsRef = useRef<TurnstileHandle>(null);

  // Wave 13.2 — surface the "senha redefinida" / "senha definida" success
  // message routed from ResetPasswordPage via location.state. The state is
  // consumed once and then cleared (via replace:true) so a refresh doesn't
  // resurrect the toast.
  const [resetNotice, setResetNotice] = useState<null | 'reset' | 'welcome'>(null);
  // P0 fix 2026-05-02 — when the API client bounces a user here because the
  // session expired (?reason=session_expired), show a calm banner instead
  // of dropping them on a clean form with no context. The bouncer also
  // appends ?next=<original-path> so we can route the user back after
  // login.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const state = location.state as { resetOk?: boolean; welcome?: boolean } | null;
    if (state?.resetOk) {
      setResetNotice(state.welcome ? 'welcome' : 'reset');
      // Strip the flag so a later refresh doesn't replay it.
      navigate(location.pathname, { replace: true, state: null });
    }
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'session_expired') setSessionExpired(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const destFor = (session: AuthSession) => {
    const fromState =
      location.state && typeof location.state === 'object' && 'from' in location.state
        ? (location.state as { from: string }).from
        : null;
    if (fromState) return fromState;
    // W123.13 · primeira vez (welcomedAt NULL) → /welcome direto. O passo
    // /post-login (escolher operação real vs playground) foi removido do
    // caminho padrão: era decisão demais pro gerente novo antes de ver a
    // 1ª obra. O modo de teste agora é um link discreto dentro do /welcome
    // (PostLoginChoicePage segue montada só pra rollback). Subsequentes:
    // respeita activeMode. Client/broker continuam direto pra /owner —
    // playground é PM-only.
    if (session.user.role === 'client' || session.user.role === 'broker') {
      return '/owner';
    }
    if (!session.user.welcomedAt) {
      return '/welcome';
    }
    if (session.user.activeMode === 'playground') {
      return '/playground';
    }
    return '/projects';
  };

  const onGoogleSuccess = (session: AuthSession) => {
    setSession(session);
    navigate(destFor(session), { replace: true });
  };

  const onGoogleError = (message: string, code?: string | null) => {
    // Wave 320b — `pending_approval` from Google now lands in the calm warn
    // banner (same as the email/password path). Brand-new signups always
    // hit this code; a red error strip on first contact reads as "broken"
    // and was the source of the "white screen" complaint.
    if (code === 'pending_approval') {
      setPendingApproval(true);
      setErrors({});
      return;
    }
    setErrors({ form: message });
    setPendingApproval(false);
  };

  // Wave 33 — when the server replies with `pending_approval`, we render
  // it as a calm warn-tone banner instead of the red error strip.
  const [pendingApproval, setPendingApproval] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = loginRequestSchema.safeParse({ email, password });
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors({
        email: flat.email?.[0] ?? '',
        password: flat.password?.[0] ?? '',
      });
      setPendingApproval(false);
      return;
    }
    setErrors({});
    setPendingApproval(false);
    setBusy(true);
    try {
      const turnstileToken = (await tsRef.current?.getToken()) ?? '';
      const session = await api.login(parsed.data, { turnstileToken });
      setSession(session);
      navigate(destFor(session), { replace: true });
    } catch (err) {
      tsRef.current?.reset();
      if (err instanceof ApiError && err.code === 'pending_approval') {
        setPendingApproval(true);
        setErrors({});
      } else {
        setErrors({
          form:
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : t.auth.badCredentials,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface text-ink">
      {/* Aurora backdrop — cinematic radial gradient + grain plate so the
          auth hero never looks flat. Positioned behind everything; the
          form card sits above. */}
      <div className="aurora" aria-hidden />
      <div className="grain" aria-hidden />

      {/* Top controls — theme + language always reachable on public pages.
          Wave Codex-R8: consolidated into PublicHeader to fix the mobile
          collision between the two floating pills and the hero content. */}
      <PublicHeader transparent />

      <div className="relative z-10 flex min-h-[calc(100vh-56px)] flex-col items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm animate-fade-in-up">
          {/* Hero lockup — single source of truth for mark + wordmark */}
          <div className="glow-brand mb-8 flex justify-center" data-active="true">
            <AnimatedLogo variant="hero" label={t.common.appName} repeatMs={30_000} />
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-line bg-surface-2 p-6 elev-2">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{t.auth.loginTitle}</h1>
            <p className="mt-1.5 text-sm text-ink-muted">{t.auth.loginSubtitle}</p>

            <div className="mt-6 space-y-4">
              {resetNotice && (
                <div
                  role="status"
                  className="animate-fade-in-up rounded-lg border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok"
                >
                  {resetNotice === 'welcome'
                    ? (isEn ? 'Password set successfully. Sign in with your email and the password you just created.' : 'Senha definida com sucesso. Entre com seu e-mail e a senha que acabou de criar.')
                    : (isEn ? 'Password reset successfully. Use your new password to sign in.' : 'Senha redefinida com sucesso. Use a nova senha para entrar.')}
                </div>
              )}

              {sessionExpired && (
                <div
                  role="status"
                  className="animate-fade-in-up rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-sm text-ink"
                >
                  {isEn ? 'Your session expired. Sign in again to pick up where you left off.' : 'Sua sessão expirou. Entre novamente para continuar de onde parou.'}
                </div>
              )}

              <GoogleSignInButton onSuccess={onGoogleSuccess} onError={onGoogleError} />

              {pendingApproval && (
                <div
                  role="status"
                  className="animate-fade-in-up rounded-lg border border-warn/30 bg-warn/10 px-3 py-3 text-sm"
                >
                  <p className="font-medium text-ink">
                    {t.wave33.login.pendingTitle}
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {t.wave33.login.pendingBody}
                  </p>
                </div>
              )}

              {errors.form && !pendingApproval && (
                <div
                  role="alert"
                  className="animate-fade-in-up rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-sm text-err"
                >
                  {errors.form}
                </div>
              )}

              {!showEmail ? (
                <button
                  type="button"
                  onClick={() => setShowEmail(true)}
                  className="pressable inline-flex min-h-11 w-full items-center justify-center px-2 text-center text-sm font-medium text-ink-muted transition-colors hover:text-ink"
                >
                  {t.auth.orContinueWithEmail}
                </button>
              ) : (
                <>
                  <div
                    className="flex items-center gap-3 text-xs uppercase tracking-wide text-ink-subtle"
                    aria-hidden="true"
                  >
                    <span className="h-px flex-1 bg-line" />
                    <span>{t.common.or}</span>
                    <span className="h-px flex-1 bg-line" />
                  </div>

                  <form onSubmit={onSubmit} className="animate-fade-in-up space-y-4" noValidate>
                    <Input
                      label={t.auth.email}
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                      error={errors.email}
                      required
                    />
                    <Input
                      label={t.auth.password}
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.currentTarget.value)}
                      error={errors.password}
                      required
                    />
                    <TurnstileWidget ref={tsRef} action="auth_login" />
                    <Button type="submit" fullWidth size="lg" loading={busy}>
                      {t.auth.loginCta}
                    </Button>
                  </form>
                </>
              )}
            </div>

            <div className="mt-5 flex flex-col items-center gap-2 text-sm text-ink-muted">
              <Link
                to="/esqueci-senha"
                className="inline-flex min-h-11 items-center px-2 font-medium text-ink underline-offset-4 hover:underline"
              >
                {isEn ? 'Forgot your password?' : 'Esqueceu a senha?'}
              </Link>
              <Link
                to="/register"
                className="inline-flex min-h-11 items-center px-2 font-medium text-ink underline-offset-4 hover:underline"
              >
                {t.auth.needAccount}
              </Link>
            </div>
          </div>

          {/* W149 · compliance footer */}
          <footer className="mt-6 text-center text-xs text-ink-muted">
            <Link to="/termos-de-uso" className="underline">{isEn ? 'Terms of use' : 'Termos de uso'}</Link>
            {' · '}
            <Link to="/politica-de-privacidade" className="underline">{isEn ? 'Privacy policy' : 'Política de privacidade'}</Link>
            {' · '}
            <span>{COMPANY_INFO.razaoSocial}</span>
          </footer>

          {/* Wave 47 — publisher credit. Intentionally small + quiet:
              the product is Builder; Nullscience is the maker. */}
          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink-subtle">
            <img
              src="/null.png"
              alt=""
              aria-hidden="true"
              width={20}
              height={20}
              className="h-5 w-5 rounded-[4px] opacity-80"
              loading="lazy"
              decoding="async"
            />
            <span>
              por <span className="text-ink-muted">Nullscience</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
